import { useState, useEffect } from "react";
import { Loader, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { getErrorMessage } from "../lib/api";
import ConfirmDialog from "./common/ConfirmDialog";
import Sheet from "./common/Sheet";
import Button from "./common/Button";
import Avatar from "./common/Avatar";
import SectionLabel from "./common/SectionLabel";

const roles = ["admin", "cfo", "project_manager", "site_manager"] as const;

export default function UserManagement({ currentUserId }: { currentUserId: string }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", full_name: "", role: "project_manager" });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get("/users/");
      setUsers(res.data.users || []);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const inviteUser = async () => {
    if (!inviteForm.email || !inviteForm.full_name) return;
    setInviteLoading(true);
    setError(null);
    setInviteSuccess(null);
    try {
      await api.post("/users/invite-token", inviteForm);
      setInviteSuccess(t("users.invitasjon_sendt", { email: inviteForm.email }));
      setInviteForm({ email: "", full_name: "", role: "project_manager" });
      setTimeout(() => { setInviteSuccess(null); setShowInvite(false); }, 4000);
      fetchUsers();
    } catch (e: any) {
      setError(getErrorMessage(e));
    } finally {
      setInviteLoading(false);
    }
  };

  const changeRole = async (userId: string, newRole: string) => {
    try {
      await api.patch(`/users/${userId}/role`, { role: newRole });
      fetchUsers();
    } catch (e) {
    }
  };

  const deleteUser = async (userId: string) => {
    try {
      await api.delete(`/users/${userId}`);
      setDeleteTarget(null);
      fetchUsers();
    } catch (e) {
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <SectionLabel className="!mt-0">{t("users.teammedlemmer")}</SectionLabel>
        <Button variant="secondary" icon={<Plus className="w-3.5 h-3.5" />} onClick={() => setShowInvite(true)}>
          {t("users.inviter_bruker")}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader className="w-5 h-5 animate-spin text-t3" />
        </div>
      ) : users.length === 0 ? (
        <p className="text-[13px] text-t3 py-6 text-center">{t("users.ingen_brukere")}</p>
      ) : (
        <div>
          {users.map((u: any) => {
            const isSelf = u.id === currentUserId;
            return (
              <div key={u.id} className="hairline-b py-4 flex items-center gap-3">
                <Avatar name={u.full_name || u.email} size="md" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-[15px] font-bold text-t1">{u.full_name}</p>
                    {isSelf && (
                      <span className="text-[10.5px] font-bold px-2 py-0.5 rounded-md bg-ios-blue/10 text-ios-blue">{t("users.deg")}</span>
                    )}
                  </div>
                  <p className="text-[12.5px] text-t2 mt-0.5">{u.email}{u.created_at && ` · ${t("users.siden")} ${u.created_at?.slice(0, 10)}`}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isSelf ? (
                    <span className="text-[13px] text-t1 font-semibold">{t(`roles.${u.role}`) || u.role}</span>
                  ) : (
                    <select
                      value={u.role}
                      onChange={(e) => changeRole(u.id, e.target.value)}
                      className="field-ios !py-2 text-[13px]"
                    >
                      {roles.map((r) => (
                        <option key={r} value={r}>{t(`roles.${r}`)}</option>
                      ))}
                    </select>
                  )}
                  {!isSelf && (
                    <button
                      onClick={() => setDeleteTarget(u)}
                      className="p-2 text-t3 hover:text-ios-red hover:bg-ios-red/10 rounded-lg transition-colors"
                      title={t("users.fjern_bruker")}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Sheet: Inviter bruker */}
      <Sheet open={showInvite} onClose={() => { setShowInvite(false); setError(null); }} title={t("users.inviter_bruker")} subtitle={t("users.inviter_sub")}>
        <div className="space-y-4 mt-4">
          <div>
            <label className="text-[12px] font-semibold text-t2 mb-1.5 block">{t("users.fullt_navn")}</label>
            <input
              value={inviteForm.full_name}
              onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })}
              placeholder="Ola Nordmann"
              className="field-ios"
              autoFocus
            />
          </div>
          <div>
            <label className="text-[12px] font-semibold text-t2 mb-1.5 block">{t("users.epost")}</label>
            <input
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              placeholder="ola@firma.no"
              type="email"
              className="field-ios"
            />
          </div>
          <div>
            <label className="text-[12px] font-semibold text-t2 mb-1.5 block">{t("users.rolle")}</label>
            <select
              value={inviteForm.role}
              onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
              className="field-ios"
            >
              {roles.map((r) => (
                <option key={r} value={r}>{t(`roles.${r}`)}</option>
              ))}
            </select>
          </div>

          {error && (
            <div className="rounded-[10px] p-3 text-[13px] bg-ios-red/10 text-ios-red">{error}</div>
          )}
          {inviteSuccess && (
            <div className="rounded-[10px] p-3 text-[13px] bg-ios-grn/10 text-ios-grn">{inviteSuccess}</div>
          )}
          <p className="text-[11px] text-t3">
            {t("users.invitasjon_gyldig")}
          </p>

          <div className="space-y-2 pt-2">
            <Button variant="primary" fullWidth loading={inviteLoading} onClick={inviteUser}>
              {t("users.send_invitasjon")}
            </Button>
            <Button variant="neutral" fullWidth onClick={() => { setShowInvite(false); setError(null); }}>
              {t("common.avbryt")}
            </Button>
          </div>
        </div>
      </Sheet>

      {deleteTarget && (
        <ConfirmDialog
          title={t("users.fjern_bruker")}
          description={t("users.fjern_bruker_desc", { name: deleteTarget.full_name, email: deleteTarget.email })}
          confirmLabel={t("users.fjern_bruker")}
          confirmColor="bg-ios-red hover:opacity-90"
          onConfirm={() => deleteUser(deleteTarget.id)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
